import { autoRetry } from "@geerly/shared";
import staticDbLoader from "../../apollo/staticDbLoader";
import { PRODUCT_PRICE_BAND_QUERY } from "../../graphql/product.query";
import {
  ANY_COLOUR_NAME,
  GENDER_DEFAULT,
  SIZE_DEFAULT,
} from "../../settings/global-website-config";
import { getDealsApiUrl } from "../products/useDeals";
import { getSizeDeals } from "../products/useSizeAndSex";

const filterForPrice = (deal) => deal.price;

export const getSizeDealsForProduct = ({
  state,
  dealsState,
  product,
  familyPriceAlerts,
  combineDeals = true,
}) => {
  if (!state) {
    throw new Error("Expected State, got none");
  }
  if (!dealsState) {
    // throw new Error('Expected dealsState, got none');
    return null;
  }
  const { sexDeals } = dealsState;

  const productDeals = sexDeals?.[product.databaseId];

  if (!productDeals) {
    return null;
  }

  const sizesDeals = productDeals.deals.colours?.[ANY_COLOUR_NAME];

  if (!sizesDeals) {
    return null;
  }
  const sizeDeals = getSizeDeals(state, sizesDeals); //sizesDeals[state.size];

  if (sizeDeals) {
    let { allDeals, familyProductDeals } = sizeDeals;

    if (allDeals) {
      allDeals = allDeals.filter(filterForPrice);
    }

    if (familyProductDeals) {
      familyProductDeals = familyProductDeals.filter(filterForPrice);
    }
    if (!combineDeals) {
      return { allDeals, familyProductDeals };
    }

    if (!familyPriceAlerts) {
      return allDeals;
    }

    if (familyProductDeals || familyPriceAlerts) {
      if (!familyProductDeals?.length) {
        return allDeals;
      }
      if (!allDeals?.length) {
        return familyProductDeals;
      }

      return allDeals
        .concat(familyProductDeals)
        .sort((a, b) => (a.price > b.price ? 1 : -1));
    }
  }

  return null;
};

const getRecommendedPriceAlertThreshold = (opts) => {
  const {
    product,
    noFallbackPricing,
    skipLiveDeals,
    priceAlertThresholdReduction,
  } = opts;
  if (!product) {
    return { recommendedThreshold: "" }; // Empty string helps the inputField behave appropriately when it's populated
  }
  const DEFAULT_PRICE_ALERT_LEVEL = 70;
  let recommendedThreshold = DEFAULT_PRICE_ALERT_LEVEL;
  if (!product) {
    return { recommendedThreshold };
  }
  if (!skipLiveDeals) {
    const sizeDeals = getSizeDealsForProduct(opts);

    if (sizeDeals?.length > 0) {
      const bestDeal = sizeDeals[0];

      const recommendedThreshold = Math.round(
        bestDeal.price - priceAlertThresholdReduction
      );

      if (recommendedThreshold && !isNaN(recommendedThreshold)) {
        // In case there's an error with the dealPrice, leave an escape hatch to continue to the fallback
        return { recommendedThreshold, bestDeal };
      }
    }
    if (noFallbackPricing) {
      return {};
    }
  }

  const { priceBand } = product;

  if (priceBand) {
    // Shouldn't get here

    recommendedThreshold = (priceBand.node.slug / 10) * 50 + 50;
  }

  const category = product.category?.node?.slug || product.category;

  if (category === "watches") {
    // Very crude ! In lieu of having cat. specific band values (TODO)

    recommendedThreshold = recommendedThreshold * 3;
  }

  return { recommendedThreshold }; // 1/10=>55, 10/10=> 100
};

export const getServerSideDeals = async ({
  state,
  dealsLocale,
  products,
  absoluteUrl,
}) => {
  if (!state.size) {
    console.log("No size in State; added default");
    state.size = SIZE_DEFAULT;
  }
  if (!state.sex) {
    console.log("No sex in state; added default");
    state.sex = GENDER_DEFAULT;
  }
  const dealsUri = getDealsApiUrl({
    isVisible: true,
    sizeAndSexState: state,
    locale: dealsLocale,
    ids: products.map((p) => p.databaseId).join(","),
    isBrowser: true,
    liteOnly: true,
    absoluteUrl,
  });
  if (!dealsUri) {
    console.error(
      `No deals URI in getServerSideDeals | ids: ${products.map(
        (p) => p.databaseId
      )} | State:`,
      state
    );
    throw new Error("No deals URI");
  }

  const fetchData = () =>
    fetch(dealsUri).then((response) => {
      if (!response.ok) throw new Error("Network response was not ok.");
      return response.json();
    });

  try {
    // Retry is necessary atm as there is an intermittent bug which will cause it to time out. Seems related to vercel & node etc:
    // https://github.com/vercel/next.js/discussions/57384
    const data = await autoRetry(fetchData, 2, 250); // retry twice; small delay
    return { sexDeals: data };
  } catch (error) {
    console.error(
      `error fetching serverSideDeals | Deals URL: ${dealsUri}`,
      error
    );
    throw error; // Rethrow to be handled or logged by the caller
  }
};

export const getDynamicRecommendedPriceAlertThreshold = async ({
  state,
  product,
  dealsLocale,
  familyPriceAlerts,
  priceAlertThresholdReduction,
  absoluteUrl,
  priceBand,
}) => {
  /* const dealsUri = getDealsApiUrl({
    isVisible: true,
    sizeAndSexState: state,
    locale: dealsLocale,
    ids: [product.databaseId],
    isBrowser: true,
    liteOnly: true,
    absoluteUrl,
  }); */
  // Set it up as a promise as we only need it later if the deals doesn't work for us;
  const fallbackDataPromise = !priceBand
    ? staticDbLoader(
        PRODUCT_PRICE_BAND_QUERY,
        {
          databaseId: product.databaseId,
        }
        //   { useBasicAuth: true }
      )
    : { data: { product: { priceBand: { node: { slug: priceBand } } } } };

  const dealsState = await getServerSideDeals({
    state,
    dealsLocale,
    products: [product],
    absoluteUrl,
  });
  const recommendedPriceAlertThreshold = getRecommendedPriceAlertThreshold({
    state,
    dealsState,
    product,
    familyPriceAlerts,
    noFallbackPricing: true,
    priceAlertThresholdReduction,
  });

  if (recommendedPriceAlertThreshold.recommendedThreshold) {
    return recommendedPriceAlertThreshold;
  }

  const { data: fallbackData } = await fallbackDataPromise;

  const fallbackProduct = { ...product, ...fallbackData.product };
  return getRecommendedPriceAlertThreshold({
    product: fallbackProduct,
    skipLiveDeals: true,
  });
};

export default getRecommendedPriceAlertThreshold;
